import type { Dispatch, FC, ReactNode, SetStateAction } from 'react';
import { createContext, useEffect, useRef, useState } from 'react';
import type { AxiosResponse } from 'axios';

import {
  NO_BACKOFF_RESULT,
  ReportOrderStatus,
  type ReportOrderStatusOrSentinel,
} from 'src/constants/report';
import type { Report } from 'src/types/report';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';

export const backoffTimes = [1, 10000, 10000, 30000, 60000, 300000];

interface ReportStatusContextProps {
  reportsStatus: ReportsStatusProps;
  setPendingReportIds: Dispatch<SetStateAction<number[]>>;
}

export const ReportStatusContext = createContext<ReportStatusContextProps>({
  reportsStatus: {},
  setPendingReportIds: () => {},
});

export type ReportStatusProps = Pick<Report, 'file' | 'order_status'>;

const getEndResult = (data: ReportsStatusProps) => {
  const result = data;
  for (const reportId in data) {
    const finalStatuses: ReportOrderStatusOrSentinel[] = [
      ReportOrderStatus.CREATED,
      ReportOrderStatus.ERROR,
    ];
    if (
      !finalStatuses.includes(
        data[reportId]?.order_status as ReportOrderStatusOrSentinel,
      )
    ) {
      Object.assign(result, {
        [reportId]: { order_status: NO_BACKOFF_RESULT },
      });
    }
  }
  return result;
};

export interface ReportsStatusProps {
  [key: number]: ReportStatusProps;
}

interface ReportStatusContextProviderProps {
  endpoint?: string;
  children: ReactNode;
}

export const ReportStatusContextProvider: FC<
  ReportStatusContextProviderProps
> = ({ children, endpoint = apiPaths.REPORT_ORDER_STATUS }) => {
  const [pendingReportIds, setPendingReportIds] = useState<number[]>([]);
  const reportsStatus = useReportOrderStatus(pendingReportIds, endpoint);

  return (
    <ReportStatusContext.Provider
      value={{ reportsStatus, setPendingReportIds }}
    >
      {children}
    </ReportStatusContext.Provider>
  );
};

export const useReportOrderStatus = (
  pendingReportIds: number[],
  endpoint = apiPaths.REPORT_ORDER_STATUS,
): ReportsStatusProps => {
  const pendingReportIdsRef = useRef<number[]>([]);
  const [reportsStatus, setReportsStatus] = useState<ReportsStatusProps>({});

  useEffect(
    () => () => {
      // clear all pending report ids on unmount to stop the exponential backoff
      pendingReportIdsRef.current = [];
    },
    [],
  );

  useEffect(() => {
    // in case all pending reports have arrived, clear all pending ids to abort the backoff
    const reportsStatusValues = Object.values(reportsStatus);
    if (
      reportsStatusValues.length &&
      reportsStatusValues.every(
        (report) => report.order_status === ReportOrderStatus.CREATED,
      )
    ) {
      pendingReportIdsRef.current = [];
    }
  }, [reportsStatus]);

  useEffect(() => {
    pendingReportIdsRef.current = pendingReportIds;
    setReportsStatus(() =>
      pendingReportIds.reduce(
        (acc, key) => ({
          ...acc,
          [key]: { order_status: ReportOrderStatus.ORDERED },
        }),
        {},
      ),
    );
    if (pendingReportIds.length) {
      exponentialBackoffOrderStatus(pendingReportIds).catch((_e) => null);
    }
  }, [pendingReportIds]);

  const exponentialBackoffOrderStatus = async (reportIds: number[]) => {
    // delays in ms - configure as desired
    for (let i = 0; i < backoffTimes.length; i++) {
      // wait for the delay before attempting an order status update
      await new Promise((resolve) => {
        setTimeout(resolve, backoffTimes[i]);
      });
      // stop running backoff on changed ids because a new backoff will start instead
      if (reportIds !== pendingReportIdsRef.current) return Promise.reject();
      await HttpService.get(endpoint, {
        report_ids: reportIds.toString(),
      }).then(({ data }: AxiosResponse<ReportsStatusProps>) => {
        if (i + 1 >= backoffTimes.length) {
          setReportsStatus(getEndResult(data));
        } else {
          setReportsStatus(data);
        }
      });
    }
  };

  return reportsStatus;
};
